import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/Ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/Ui/Dialog';

interface ConfirmDialogProps {
    children?: ReactNode;
    okLabel?: string;
    onConfirm?: () => unknown;
}

const ConfirmDialog = function ({ children, okLabel = 'Confirm', onConfirm = undefined }: ConfirmDialogProps) {
    const [
        t,
    ] = useTranslation('pages');
    const location = useLocation();
    const navigate = useNavigate();
    const [
        searchParams,
    ] = useSearchParams();

    const [
        submitting,
        setSubmitting,
    ] = useState(false);

    const close = () => {
        navigate({
            pathname: location.pathname,
            search: createSearchParams(searchParams).toString(),
        },
        { state: { confirmed: false }, replace: true });
    };

    const submit = () => {
        if (onConfirm) {
            setSubmitting(true);

            Promise.resolve(onConfirm()).then((shouldClose) => {
                if (shouldClose) {
                    close();
                }

                setSubmitting(false);
            }).catch(() => {
                setSubmitting(false);
            });
        }

        const { dialogAction: _dialogAction, ...otherStateParams } = (location.state ?? {}) as Record<string, unknown>;

        navigate(
            {
                pathname: location.pathname,
                search: createSearchParams(searchParams).toString(),
            },
            { state: { ...otherStateParams, confirmed: true }, replace: true }
        );
    };

    return (
        <Dialog onOpenChange={ (open) => {
            if (open === false) {
                close();
            }
        } } open={ true }>
            <DialogContent className='sm:max-w-[500px]'>
                <DialogHeader>
                    <DialogTitle>{t('Confirmation')}</DialogTitle>
                    <DialogDescription as='div' className='min-h-24 py-4'>
                        {children}
                    </DialogDescription>
                </DialogHeader>
                <div className='bg-muted px-4 py-3 flex flex-row-reverse sm:px-6 gap-x-4 justify-center sm:justify-start'>
                    <Button disabled={ submitting } onClick={ submit }>{okLabel}</Button>
                    <Button onClick={ close } variant='outline'>{t('Cancel')}</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ConfirmDialog;
